import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { getBrandById, getOrganizerById, updateMatchStatus } from '../../services/dataService';
import { getOrCreateConversation, sendMessage } from '../../services/chatService';
import { Match } from '../../services/matchingService';
import { Message } from '../../services/chatService';
import { Button } from '../../components/Button';
import { CheckIcon, XIcon, MessageSquareIcon, ArrowLeftIcon, FileTextIcon, CheckCircleIcon } from 'lucide-react';
import { ContractForm, ContractDetails } from '../../components/ContractForm';
export function MatchDetails() {
  const {
    matchId
  } = useParams<{
    matchId: string;
  }>();
  const {
    currentUser
  } = useAuth();
  const navigate = useNavigate();
  const [match, setMatch] = useState<Match | null>(null);
  const [brand, setBrand] = useState<any | null>(null);
  const [organizer, setOrganizer] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState<'brand' | 'organizer'>('brand');
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [conversation, setConversation] = useState<any | null>(null);
  const [showContractForm, setShowContractForm] = useState(false);
  const [contract, setContract] = useState<any | null>(null);
  useEffect(() => {
    const loadData = async () => {
      if (!matchId || !currentUser) return;
      // Get match data
      const matches = JSON.parse(localStorage.getItem('matches') || '[]') as Match[];
      const foundMatch = matches.find(m => m.id === matchId);
      if (!foundMatch) {
        navigate('/dashboard');
        return;
      }
      setMatch(foundMatch);
      // Get brand and organizer data
      const brandData = getBrandById(foundMatch.brandId);
      const organizerData = getOrganizerById(foundMatch.organizerId);
      setBrand(brandData);
      setOrganizer(organizerData);
      // Determine user type
      if (brandData && brandData.userId === currentUser.id) {
        setUserType('brand');
      } else if (organizerData && organizerData.userId === currentUser.id) {
        setUserType('organizer');
      }
      // Get conversation
      if (brandData && organizerData) {
        const conv = getOrCreateConversation(brandData.id, organizerData.id);
        setConversation(conv);
        setMessages(conv.messages);
      }
      // Check if there's an existing contract
      const contracts = JSON.parse(localStorage.getItem('contracts') || '[]');
      const existingContract = contracts.find((c: any) => c.matchId === matchId);
      if (existingContract) {
        setContract(existingContract);
      }
      setLoading(false);
    };
    loadData();
  }, [matchId, currentUser, navigate]);
  const handleStatusChange = (status: 'accepted' | 'rejected') => {
    if (!match) return;
    const updatedMatch = updateMatchStatus(match.id, status);
    setMatch(updatedMatch);
  };
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!conversation || !newMessage.trim() || !currentUser) return;
    sendMessage(conversation.id, currentUser.id, userType, newMessage);
    setNewMessage('');
    // Refresh messages
    const conv = JSON.parse(localStorage.getItem('conversations') || '[]').find((c: any) => c.id === conversation.id);
    if (conv) {
      setMessages(conv.messages);
    }
  };
  const handleContractCreated = (contractData: any) => {
    setContract(contractData);
    setShowContractForm(false);
  };
  if (loading) {
    return <DashboardLayout userType={userType} data-id="element-1829">
        <div className="flex justify-center items-center h-64" data-id="element-1830">
          <div className="text-gray-500" data-id="element-1831">Laddar...</div>
        </div>
      </DashboardLayout>;
  }
  if (!match || !brand || !organizer) {
    return <DashboardLayout userType={userType} data-id="element-1832">
        <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1833">
          <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1834">
            Match hittades inte
          </h2>
          <p className="text-gray-600 mb-4" data-id="element-1835">
            Matchen du letar efter finns inte eller så har du inte behörighet
            att visa den.
          </p>
          <button onClick={() => navigate(-1)} className="flex items-center text-indigo-600 hover:text-indigo-800" data-id="element-1836">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-1837" />
            Gå tillbaka
          </button>
        </div>
      </DashboardLayout>;
  }
  return <DashboardLayout userType={userType} data-id="element-1838">
      <div className="mb-6" data-id="element-1839">
        <button onClick={() => navigate(-1)} className="flex items-center text-indigo-600 hover:text-indigo-800" data-id="element-1840">
          <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-1841" />
          Tillbaka till matchningar
        </button>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-1842">
        <div className="flex justify-between items-center mb-4" data-id="element-1843">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1844">
            {userType === 'brand' ? organizer.eventName : brand.companyName}
          </h1>
          <div className={`px-3 py-1 inline-flex items-center rounded-full ${match.score >= 80 ? 'bg-green-100 text-green-800' : match.score >= 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1845">
            <span className="text-sm font-medium" data-id="element-1846">{match.score}% Match</span>
          </div>
        </div>
        {/* Match status actions */}
        {match.status === 'pending' && <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6" data-id="element-1847">
            <div className="flex items-start" data-id="element-1848">
              <div className="flex-shrink-0" data-id="element-1849">
                <AlertCircleIcon className="h-5 w-5 text-yellow-400" data-id="element-1850" />
              </div>
              <div className="ml-3" data-id="element-1851">
                <h3 className="text-sm font-medium text-yellow-800" data-id="element-1852">
                  {userType === 'brand' ? 'Ny sponsringsmöjlighet' : 'Ny sponsormatch'}
                </h3>
                <div className="mt-2 text-sm text-yellow-700" data-id="element-1853">
                  <p data-id="element-1854">
                    {userType === 'brand' ? 'Du har matchats med detta event baserat på dina preferenser och krav.' : 'Du har matchats med detta varumärke baserat på dina eventdetaljer och deras sponsringskriterier.'}
                  </p>
                </div>
                <div className="mt-4" data-id="element-1855">
                  <div className="flex space-x-3" data-id="element-1856">
                    <Button variant="primary" onClick={() => handleStatusChange('accepted')} className="flex items-center" data-id="element-1857">
                      <CheckIcon className="h-4 w-4 mr-1" data-id="element-1858" />
                      Acceptera match
                    </Button>
                    <Button variant="secondary" onClick={() => handleStatusChange('rejected')} className="flex items-center" data-id="element-1859">
                      <XIcon className="h-4 w-4 mr-1" data-id="element-1860" />
                      Avböj
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>}
        {match.status === 'accepted' && <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6" data-id="element-1861">
            <div className="flex items-start" data-id="element-1862">
              <div className="flex-shrink-0" data-id="element-1863">
                <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-1864" />
              </div>
              <div className="ml-3" data-id="element-1865">
                <h3 className="text-sm font-medium text-green-800" data-id="element-1866">
                  Match accepterad
                </h3>
                <div className="mt-2 text-sm text-green-700" data-id="element-1867">
                  <p data-id="element-1868">
                    {userType === 'brand' ? 'Du har accepterat denna sponsringsmöjlighet. Använd meddelandesystemet nedan för att diskutera detaljer.' : 'Detta varumärke har bekräftats som sponsor för ditt event. Använd meddelandesystemet nedan för att diskutera detaljer.'}
                  </p>
                </div>
                {!contract && match.status === 'accepted' && <div className="mt-4" data-id="element-1869">
                    <Button variant="outline" onClick={() => setShowContractForm(true)} className="flex items-center" data-id="element-1870">
                      <FileTextIcon className="h-4 w-4 mr-1" data-id="element-1871" />
                      Skapa sponsringsavtal
                    </Button>
                  </div>}
              </div>
            </div>
          </div>}
        {match.status === 'rejected' && <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6" data-id="element-1872">
            <div className="flex items-start" data-id="element-1873">
              <div className="flex-shrink-0" data-id="element-1874">
                <XIcon className="h-5 w-5 text-red-400" data-id="element-1875" />
              </div>
              <div className="ml-3" data-id="element-1876">
                <h3 className="text-sm font-medium text-red-800" data-id="element-1877">
                  Match avböjd
                </h3>
                <div className="mt-2 text-sm text-red-700" data-id="element-1878">
                  <p data-id="element-1879">
                    {userType === 'brand' ? 'Du har avböjt denna sponsringsmöjlighet.' : 'Detta varumärke har avböjt att sponsra ditt event.'}
                  </p>
                </div>
              </div>
            </div>
          </div>}
        {/* Match reasons */}
        <div className="mb-6" data-id="element-1880">
          <h2 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1881">
            Varför ni matchade
          </h2>
          <ul className="space-y-2" data-id="element-1882">
            {match.matchReasons.map((reason, index) => <li key={index} className="flex items-center" data-id="element-1883">
                <div className="bg-indigo-100 rounded-full p-1 mr-2" data-id="element-1884">
                  <CheckIcon className="h-4 w-4 text-indigo-600" data-id="element-1885" />
                </div>
                <span className="text-gray-700" data-id="element-1886">{reason}</span>
              </li>)}
          </ul>
        </div>
        {/* Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1887">
          {userType === 'brand' ? <div data-id="element-1888">
              <h2 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1889">
                Eventdetaljer
              </h2>
              <div className="space-y-4" data-id="element-1890">
                <div data-id="element-1891">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1892">
                    Eventnamn
                  </h3>
                  <p className="text-gray-900" data-id="element-1893">{organizer.eventName}</p>
                </div>
                <div data-id="element-1894">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1895">
                    Eventbeskrivning
                  </h3>
                  <p className="text-gray-900" data-id="element-1896">{organizer.elevatorPitch}</p>
                </div>
                <div data-id="element-1897">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1898">Datum</h3>
                  <p className="text-gray-900" data-id="element-1899">
                    {new Date(organizer.eventDate).toLocaleDateString()}
                  </p>
                </div>
                <div data-id="element-1900">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1901">Plats</h3>
                  <p className="text-gray-900" data-id="element-1902">{organizer.location}</p>
                </div>
                <div data-id="element-1903">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1904">
                    Publikstorlek
                  </h3>
                  <p className="text-gray-900" data-id="element-1905">
                    {organizer.attendeeCount === 'under_100' ? 'Under 100' : organizer.attendeeCount === '100_500' ? '100 - 500' : organizer.attendeeCount === '500_1000' ? '500 - 1 000' : organizer.attendeeCount === '1000_5000' ? '1 000 - 5 000' : organizer.attendeeCount === '5000_plus' ? '5 000+' : 'Ej angivet'}
                  </p>
                </div>
                <div data-id="element-1906">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1907">
                    Publikbeskrivning
                  </h3>
                  <p className="text-gray-900" data-id="element-1908">
                    {organizer.audienceDescription}
                  </p>
                </div>
                <div data-id="element-1909">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1910">
                    Kontaktinformation
                  </h3>
                  <p className="text-gray-900" data-id="element-1911">
                    {organizer.contactName}
                    <br data-id="element-1912" />
                    {organizer.email}
                    <br data-id="element-1913" />
                    {organizer.phone}
                  </p>
                </div>
                <div data-id="element-1914">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1915">Adress</h3>
                  <p className="text-gray-900" data-id="element-1916">
                    {organizer.address}
                    <br data-id="element-1917" />
                    {organizer.postalCode} {organizer.city}
                  </p>
                </div>
              </div>
            </div> : <div data-id="element-1918">
              <h2 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1919">
                Varumärkesdetaljer
              </h2>
              <div className="space-y-4" data-id="element-1920">
                <div data-id="element-1921">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1922">
                    Företagsnamn
                  </h3>
                  <p className="text-gray-900" data-id="element-1923">{brand.companyName}</p>
                </div>
                <div data-id="element-1924">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1925">Produkt</h3>
                  <p className="text-gray-900" data-id="element-1926">{brand.productName}</p>
                </div>
                <div data-id="element-1927">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1928">
                    Produktbeskrivning
                  </h3>
                  <p className="text-gray-900" data-id="element-1929">{brand.productDescription}</p>
                </div>
                <div data-id="element-1930">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1931">Bransch</h3>
                  <p className="text-gray-900" data-id="element-1932">
                    {brand.industry === 'food_beverage' ? 'Livsmedel & Dryck' : brand.industry === 'beauty_cosmetics' ? 'Skönhet & Kosmetika' : brand.industry === 'health_wellness' ? 'Hälsa & Välmående' : brand.industry === 'tech' ? 'Teknik' : brand.industry === 'fashion' ? 'Mode & Kläder' : brand.industry === 'home_goods' ? 'Hem & Inredning' : brand.industry === 'sports_fitness' ? 'Sport & Fitness' : brand.industry === 'entertainment' ? 'Underhållning' : 'Annat'}
                  </p>
                </div>
                <div data-id="element-1933">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1934">
                    Sponsringstyp
                  </h3>
                  <p className="text-gray-900" data-id="element-1935">
                    {brand.sponsorshipType.map((type: string) => type === 'product_sampling' ? 'Produktprovning' : type === 'financial_sponsorship' ? 'Finansiell sponsring' : type === 'in_kind_goods' ? 'Varor' : type === 'merchandise' ? 'Merchandise' : type === 'experience' ? 'Varumärkesupplevelse' : type).join(', ')}
                  </p>
                </div>
                <div data-id="element-1936">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1937">
                    Kontaktinformation
                  </h3>
                  <p className="text-gray-900" data-id="element-1938">
                    {brand.contactName}
                    <br data-id="element-1939" />
                    {brand.email}
                    <br data-id="element-1940" />
                    {brand.phone}
                  </p>
                </div>
                <div data-id="element-1941">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1942">Adress</h3>
                  <p className="text-gray-900" data-id="element-1943">
                    {brand.address}
                    <br data-id="element-1944" />
                    {brand.postalCode} {brand.city}
                  </p>
                </div>
              </div>
            </div>}
          <div data-id="element-1945">
            <h2 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1946">
              {userType === 'brand' ? 'Vad de behöver' : 'Vad de erbjuder'}
            </h2>
            {userType === 'brand' ? <div className="space-y-4" data-id="element-1947">
                <div data-id="element-1948">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1949">
                    Sponsringsbehov
                  </h3>
                  <p className="text-gray-900" data-id="element-1950">{organizer.sponsorshipNeeds}</p>
                </div>
                <div data-id="element-1951">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1952">
                    Erbjudanden för sponsorer
                  </h3>
                  <p className="text-gray-900" data-id="element-1953">
                    {organizer.offeringTypes.map((type: string) => type === 'brand_visibility' ? 'Varumärkessynlighet' : type === 'content_creation' ? 'Innehållsskapande' : type === 'lead_generation' ? 'Leadgenerering' : type === 'product_sampling' ? 'Produktprovning' : type === 'product_feedback' ? 'Produktfeedback' : type === 'merchandise_sales' ? 'Merchandise-försäljning' : type).join(', ')}
                  </p>
                </div>
                {organizer.brandVisibility && <div data-id="element-1954">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-1955">
                      Detaljer om varumärkessynlighet
                    </h3>
                    <p className="text-gray-900" data-id="element-1956">{organizer.brandVisibility}</p>
                  </div>}
                {organizer.bonusValueDetails && <div data-id="element-1957">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-1958">
                      Ytterligare värde
                    </h3>
                    <p className="text-gray-900" data-id="element-1959">
                      {organizer.bonusValueDetails}
                    </p>
                  </div>}
              </div> : <div className="space-y-4" data-id="element-1960">
                <div data-id="element-1961">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1962">
                    Marknadsföringsmål
                  </h3>
                  <p className="text-gray-900" data-id="element-1963">{brand.marketingGoals}</p>
                </div>
                <div data-id="element-1964">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1965">
                    Budgetintervall
                  </h3>
                  <p className="text-gray-900" data-id="element-1966">
                    {brand.budget === 'under_10000' ? 'Under 10 000 kr' : brand.budget === '10000_50000' ? '10 000 - 50 000 kr' : brand.budget === '50000_100000' ? '50 000 - 100 000 kr' : brand.budget === '100000_250000' ? '100 000 - 250 000 kr' : brand.budget === '250000_plus' ? '250 000 kr+' : 'Ej angivet'}
                  </p>
                </div>
                <div data-id="element-1967">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-1968">
                    Tillgänglig kvantitet
                  </h3>
                  <p className="text-gray-900" data-id="element-1969">
                    {brand.productQuantity || 'Ej angivet'}
                  </p>
                </div>
                {brand.hasTestPanels === 'yes' && <div data-id="element-1970">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-1971">
                      Testpaneler & Samplingaktiviteter
                    </h3>
                    <p className="text-gray-900" data-id="element-1972">{brand.testPanelDetails}</p>
                  </div>}
              </div>}
          </div>
        </div>
      </div>
      {/* Contract section */}
      {showContractForm && match.status === 'accepted' && <div className="mb-6" data-id="element-1973">
          <ContractForm brandName={brand.companyName} organizerName={organizer.organizerName} eventName={organizer.eventName} matchId={match.id} onContractCreated={handleContractCreated} data-id="element-1974" />
        </div>}
      {contract && <div className="mb-6" data-id="element-1975">
          <ContractDetails contract={contract} data-id="element-1976" />
        </div>}
      {/* Messaging */}
      <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1977">
        <div className="flex items-center mb-4" data-id="element-1978">
          <MessageSquareIcon className="h-5 w-5 text-indigo-600 mr-2" data-id="element-1979" />
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-1980">Meddelanden</h2>
        </div>
        <div className="border rounded-lg mb-4" data-id="element-1981">
          <div className="h-64 overflow-y-auto p-4 space-y-3" data-id="element-1982">
            {messages.length === 0 ? <div className="text-center text-gray-500 py-8" data-id="element-1983">
                Inga meddelanden än. Starta konversationen!
              </div> : messages.map(message => <div key={message.id} className={`flex ${message.senderType === userType ? 'justify-end' : 'justify-start'}`} data-id="element-1984">
                  <div className={`max-w-3/4 rounded-lg p-3 ${message.senderType === 'ai' ? 'bg-gray-100 text-gray-800' : message.senderType === userType ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-800'}`} data-id="element-1985">
                    {message.senderType === 'ai' && <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-1986">
                        AI Assistent
                      </div>}
                    <p className="text-sm" data-id="element-1987">{message.content}</p>
                    <div className="text-xs mt-1 text-right opacity-70" data-id="element-1988">
                      {new Date(message.timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                    </div>
                  </div>
                </div>)}
          </div>
          <form onSubmit={handleSendMessage} className="border-t p-3 flex" data-id="element-1989">
            <input type="text" placeholder="Skriv ett meddelande..." className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={newMessage} onChange={e => setNewMessage(e.target.value)} disabled={match.status === 'rejected'} data-id="element-1990" />
            <Button type="submit" variant="primary" className="ml-2" disabled={!newMessage.trim() || match.status === 'rejected'} data-id="element-1991">
              Skicka
            </Button>
          </form>
        </div>
        <div className="text-xs text-gray-500" data-id="element-1992">
          <p data-id="element-1993">
            Behöver du hjälp med din konversation? Vår AI-assistent kommer att
            föreslå hjälpsamma svar när du ställer frågor.
          </p>
        </div>
      </div>
    </DashboardLayout>;
}
function AlertCircleIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1994">
      <circle cx="12" cy="12" r="10" data-id="element-1995" />
      <line x1="12" y1="8" x2="12" y2="12" data-id="element-1996" />
      <line x1="12" y1="16" x2="12.01" y2="16" data-id="element-1997" />
    </svg>;
}